import { format, parseISO } from "date-fns";
import ReactECharts from "echarts-for-react";
import PropTypes from "prop-types";
import React, { useMemo } from "react";

const DATE_FORMATS = {
  week: "dd/MM",
  default: "HH:mm",
};

const getDateFormat = (range) => DATE_FORMATS[range] || DATE_FORMATS.default;

const formatTimestamp = (timestamp, range) =>
  format(parseISO(timestamp), getDateFormat(range));

const createEmptyDataPoint = (index) => [index, null, null, null, null, null];

const createDataPoint = (item, currentIndex) => ({
  idx: currentIndex,
  open: item.open,
  close: item.close,
  low: item.min,
  high: item.max,
  trend: item.trend,
});

function formatChartData(dataPoints, range) {
  const labels = [];
  const data = [];
  let prevLabel = null;
  let currentIndex = -1;

  (dataPoints || []).forEach((item) => {
    const label = formatTimestamp(item.timestamp, range);

    if (label !== prevLabel) {
      labels.push(label);
      currentIndex += 1;
      prevLabel = label;
    }

    if (item.type === "empty") {
      data.push(createEmptyDataPoint(currentIndex));
      return;
    }

    data.push(createDataPoint(item, currentIndex));
  });

  return { labels, data };
}

const createTooltipFormatter = (labels, unit) => (params) => {
  const { idx, open, close, low, high } = params.data;

  if (open == null) return "";

  return `
    <b>${labels[idx]}</b><br/>
    Open: ${open}${unit}<br/>
    Close: ${close}${unit}<br/>
    Low: ${low}${unit}<br/>
    High: ${high}${unit}
  `;
};

const createCandlestickLine = (x, y1, y2, color, lineWidth = 2) => ({
  type: "line",
  shape: { x1: x, y1, x2: x, y2 },
  style: { stroke: color, lineWidth },
});

const createCandlestickBody = (x, yOpen, yClose, color, bodyWidth) => ({
  type: "rect",
  shape: {
    x: x - bodyWidth / 2,
    y: Math.min(yOpen, yClose),
    width: bodyWidth,
    height: Math.max(Math.abs(yOpen - yClose), 2),
  },
  style: { fill: color },
});

const createRenderFunction = (getColorFn, bodyWidth) => (params, api) => {
  const value = api.value();
  const { idx, open, close, low, high, trend } = value;

  if (open == null) return null;

  const x = api.coord([idx, 0])[0];
  const yOpen = api.coord([0, open])[1];
  const yClose = api.coord([0, close])[1];
  const yLow = api.coord([0, low])[1];
  const yHigh = api.coord([0, high])[1];

  const color = getColorFn({ open, close, low, high, trend });

  return {
    type: "group",
    children: [
      createCandlestickLine(x, yHigh, Math.min(yOpen, yClose), color),
      createCandlestickLine(x, Math.max(yOpen, yClose), yLow, color),
      createCandlestickBody(x, yOpen, yClose, color, bodyWidth),
    ],
  };
};

const createChartOption = (labels, data, config) => ({
  grid: config.grid,
  xAxis: {
    type: "category",
    data: labels,
    axisLine: { show: true },
    axisTick: { show: true },
  },
  yAxis: {
    type: "value",
    min: config.yAxisMin,
    max: config.yAxisMax,
    axisLine: { show: true, lineStyle: { width: 1 } },
    axisTick: { show: true },
    axisLabel: { margin: 8 },
    splitLine: { show: true, lineStyle: { type: "dashed" } },
  },
  tooltip: {
    trigger: "item",
    formatter: createTooltipFormatter(labels, config.unit),
  },
  series: [
    {
      type: "custom",
      data,
      renderItem: createRenderFunction(config.getColor, config.bodyWidth),
    },
  ],
});

function CandlestickChart({ data: chartData, config }) {
  const { labels, data } = useMemo(
    () => formatChartData(chartData?.data_points, chartData?.range),
    [chartData]
  );

  const option = useMemo(
    () => createChartOption(labels, data, config),
    [labels, data, config]
  );

  return <ReactECharts option={option} style={{ height: config.height }} />;
}

CandlestickChart.propTypes = {
  data: PropTypes.shape({
    range: PropTypes.string,
    data_points: PropTypes.arrayOf(
      PropTypes.shape({
        timestamp: PropTypes.string,
        open: PropTypes.number,
        close: PropTypes.number,
        high: PropTypes.number,
        low: PropTypes.number,
        trend: PropTypes.string,
        type: PropTypes.string,
      })
    ),
  }),
  config: PropTypes.shape({
    height: PropTypes.number,
    bodyWidth: PropTypes.number,
    yAxisMin: PropTypes.number,
    yAxisMax: PropTypes.number,
    grid: PropTypes.shape({
      left: PropTypes.number,
      right: PropTypes.number,
      top: PropTypes.number,
      bottom: PropTypes.number,
    }),
    unit: PropTypes.string,
    getColor: PropTypes.func.isRequired,
  }).isRequired,
};

CandlestickChart.defaultProps = {
  data: {
    range: "hour",
    data_points: [],
  },
};

export default CandlestickChart;
